/**
 * Client side of checkout telemetry (lp-checkout-repair A6, criterion 9).
 * Fire-and-forget: it returns nothing, never throws and never blocks checkout.
 * The body holds only the allowlisted event name plus a known pack id, a PayFast
 * uuid and a fixed-literal detail; the server derives amount and user itself.
 */
import { useAuthStore } from '../../store/useAuthStore';
import { CHECKOUT_EVENTS, type CheckoutEvent } from '../../../lib/checkoutEvents';
import { isPaygPackId } from '../../../lib/paygPacks';

export interface CheckoutEventExtra {
  pack_id?: string;
  payfast_uuid?: string;
  detail?: string;
}

export function trackCheckoutEvent(event: CheckoutEvent, extra: CheckoutEventExtra = {}): void {
  try {
    if (!(CHECKOUT_EVENTS as readonly string[]).includes(event)) return;
    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;
    const body: Record<string, string> = { event };
    if (isPaygPackId(extra.pack_id)) body.pack_id = extra.pack_id;
    if (typeof extra.payfast_uuid === 'string') body.payfast_uuid = extra.payfast_uuid;
    if (typeof extra.detail === 'string') body.detail = extra.detail;
    void Promise.resolve(
      fetch('/api/profile/checkout-event', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    ).catch(() => {});
  } catch {
    /* telemetry must never affect checkout */
  }
}

export type CouponResult = 'applied' | 'expired' | 'invalid' | 'not_discount';

/** Sends coupon_applied or coupon_rejected for a coupon-validate response; never the code itself. */
export function trackCouponResult(data: { valid?: boolean; reason?: string; type?: string }): CouponResult {
  if (!data.valid) {
    const result = data.reason === 'expired' ? 'expired' : 'invalid';
    trackCheckoutEvent('coupon_rejected', { detail: result });
    return result;
  }
  if (data.type !== 'discount') {
    trackCheckoutEvent('coupon_rejected', { detail: 'not_discount' });
    return 'not_discount';
  }
  trackCheckoutEvent('coupon_applied');
  return 'applied';
}

/** The coupon check itself failed (network, bad response). */
export function trackCouponError(): void {
  trackCheckoutEvent('coupon_rejected', { detail: 'error' });
}
